<script setup lang="ts">
import { Link, router, usePage } from '@inertiajs/vue3';
import { ArrowLeft, Plus, Sparkles } from '@lucide/vue';
import { useResizeObserver } from '@vueuse/core';
import { computed, defineAsyncComponent, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { Button } from '@/components/ui/button/index';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip/index';
import AiUploadModal from '@ja/ai/components/AiUploadModal.vue';
import { getSessionSettings, setSessionSettings } from '@ja/portal-query';

import { useListerContext } from '../composables/useCrudContext';
import { useTranslatable } from '../composables/useTranslatable';
import type { SharedCrudData } from '../types';
import { create, edit } from '../wayfinder/routes';

// Lazy-loaded core lister logic (includes TanStack Table)
const ListerView = defineAsyncComponent(() => import('./ListerView.vue'));
const BlockConfig = defineAsyncComponent(() => import('./ConfigBuilder/BlockConfig.vue'));

defineProps<{
    // Accept context as optional if component is reused manually
    model?: string;
    config?: any;
    items?: any[];
    pagination?: any;
    filters?: any;
}>();

const contextProps = useListerContext();
const page = usePage<{ crud: SharedCrudData }>();
const { t } = useI18n();

// Config Shortcuts
const activeConfig = computed(() => contextProps.config);
const { trField, trBlock, te, t: trT } = useTranslatable(activeConfig);

const titles = computed(() => {
    const prefix = activeConfig.value?.translationPrefix;
    const fallbackSingular = activeConfig.value?.titles.singular ?? '';
    const fallbackPlural = activeConfig.value?.titles.plural ?? '';
    
    if (!prefix) return { singular: fallbackSingular, plural: fallbackPlural };
    
    return {
        singular: te(`models.${prefix}.singular`) ? trT(`models.${prefix}.singular`) : fallbackSingular,
        plural: te(`models.${prefix}.plural`) ? trT(`models.${prefix}.plural`) : fallbackPlural,
    };
});
// Use context model with fallback to shared crud.current_model
const activeModel = computed(() => contextProps.model ?? page.props.crud?.current_model ?? null);

// View Mode State
const settings = getSessionSettings();
const viewMode = ref<'table' | 'cards' | 'grid'>(settings.listerViewMode ?? 'table');

// Persistence
watch(viewMode, (newMode) => {
    void setSessionSettings({ listerViewMode: newMode });
});

// Responsive Auto-Switch
const containerRef = ref<HTMLElement | null>(null);
const containerWidth = ref(1000);

useResizeObserver(containerRef, (entries) => {
    const entry = entries[0];
    const { width } = entry.contentRect;
    containerWidth.value = width;

    // Auto-switch to cards on small screens
    if (width < 768 && viewMode.value === 'table') {
        viewMode.value = 'cards';
    }
});

// Create route with parent context preserved
// Only generate URL if we have a valid model name
const createRoute = computed(() => {
    if (!activeModel.value) {
        return null;
    }
    const baseUrl = create({ model: activeModel.value }).url;
    if (contextProps.parentContext) {
        const params = new URLSearchParams({
            [contextProps.parentContext.foreignKey]: contextProps.parentContext.id,
        });
        return `${baseUrl}?${params.toString()}`;
    }
    return baseUrl;
});

// Parent edit route for breadcrumb navigation
const parentEditRoute = computed(() => {
    if (!contextProps.parentContext) return '';
    return edit({
        model: contextProps.parentContext.model,
        id: contextProps.parentContext.id,
    }).url;
});

// Get parent display title
const parentDisplayTitle = computed(() => {
    if (!contextProps.parentContext?.item) return contextProps.parentContext?.id ?? '';
    const titleField = contextProps.parentContext.config?.editor?.titleField || 'id';
    return contextProps.parentContext.item[titleField] ?? contextProps.parentContext.id;
});

const isMounted = ref(false);
onMounted(() => {
    isMounted.value = true;
});

// AI Upload
const aiConfig = computed(() => activeConfig.value?.ai ?? null);
const aiUploadOpen = ref(false);

function onAiCompleted() {
    router.reload({ only: ['items', 'pagination'] });
}
</script>

<template>
    <div class="space-y-4" ref="containerRef">
        <!-- Header -->
        <Teleport v-if="isMounted" to="#header-portal">
            <div class="flex items-center gap-4 px-2">
                <!-- Parent breadcrumb for child models -->
                <template v-if="contextProps.parentContext">
                    <Link :href="parentEditRoute">
                        <Button variant="ghost" size="icon-sm">
                            <ArrowLeft class="size-4" />
                        </Button>
                    </Link>
                    <div class="min-w-0">
                        <h1 class="truncate text-lg font-bold tracking-tight">
                            {{ titles.plural }}
                        </h1>
                        <p class="text-muted-foreground truncate text-xs">
                            {{ contextProps.parentContext.config?.titles.singular }}:
                            {{ parentDisplayTitle }}
                        </p>
                    </div>
                </template>
                <template v-else>
                    <h1 class="truncate text-xl font-bold tracking-tight">
                        {{ titles.plural }}
                    </h1>
                </template>
                <TooltipProvider v-if="aiConfig?.enabled" :delay-duration="300">
                    <Tooltip>
                        <TooltipTrigger as-child>
                            <Button size="icon-sm" variant="outline" @click="aiUploadOpen = true">
                                <Sparkles class="size-4" />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">
                            {{ aiConfig.label ?? 'Import from document' }}
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
                <TooltipProvider v-if="createRoute" :delay-duration="300">
                    <Tooltip>
                        <TooltipTrigger as-child>
                            <Button as-child size="icon-sm" variant="default">
                                <Link
                                    :href="createRoute"
                                    :aria-label="t('crud.create', { singular: titles.singular })"
                                >
                                    <Plus class="size-4" />
                                </Link>
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">
                            {{ t('crud.create', { singular: titles.singular }) }}
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </div>
        </Teleport>

        <!-- Core View (Lazy Loaded) -->
        <ListerView v-model:view-mode="viewMode" />

        <!-- Custom Blocks (Global Domain UI for Lister) -->
        <template v-if="activeConfig?.lister?.blocks">
            <BlockConfig
                v-for="block in activeConfig.lister.blocks"
                :key="block.key"
                :block="block"
                :model="activeModel"
                :form="{}"
                :errors="{}"
                :tr-field="trField"
                :tr-block="trBlock"
                :translation-prefix="activeConfig?.translationPrefix"
            />
        </template>
    </div>

    <!-- AI Upload Modal -->
    <AiUploadModal
        v-if="aiConfig?.enabled && activeModel && activeConfig?.model"
        v-model:open="aiUploadOpen"
        :model-key="activeModel"
        :model-class="activeConfig.model"
        :label="aiConfig.label"
        :description="aiConfig.description"
        :accepted-mime-types="aiConfig.accepted_mime_types"
        :max-file-size-mb="aiConfig.max_file_size_mb"
        :multiple="aiConfig.multiple"
        @completed="onAiCompleted"
    />
</template>
